import { Component, OnInit, ViewChild, AfterViewInit, Inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LmrServices } from '../../services/lmr.service';
import { ToastrService } from 'ngx-toastr';
import Utils from '@app/shared/utils/utils';
import {
  ImportResumeData,
  ImportResumeDialogComponent,
} from '@app/shared/components/import-resume-dialog/import-resume-dialog.component';
import { Observable } from 'rxjs';

export interface ImportItem {
  id: string;
  partner: string;
  user: string;
  dateTime: string;
  downloadUrl: string;
}

export interface DialogConfig {
  partners?: any[];
  mode: 'full' | 'simple';
  textDialog: string;
  importFn: (file: File, partnerId?: string) => Observable<any>;
}

@Component({
  selector: 'app-dialog-import',
  templateUrl: './dialog-import.component.html',
  styleUrls: ['./dialog-import.component.sass'],
})
export class DialogImportComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = ['partner', 'user', 'dateTime', 'actions'];
  dataSource = new MatTableDataSource<ImportItem>([]);
  partnerSelected: string = 'TODOS';
  fileSelected!: File;
  fileName: string = '';
  partners: any[] = [];
  showHistory: boolean = true;
  showPartnerSelect: boolean = true;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('fileInput') fileInput!: any;
  textDialog: string = '';

  constructor(
    private dialogRef: MatDialogRef<DialogImportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private lmrService: LmrServices,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {}

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;
    const isExcel = file.name.endsWith('.xlsx');

    if (!isExcel) {
      this.toastr.error('Selecione um arquivo válido (.xlsx)');
      return;
    }
    this.fileSelected = file;
    this.fileName = file.name;
  }

  ngOnInit(): void {
    const config = this.data as DialogConfig;

    if (config.mode === 'simple') {
      this.showHistory = false;
      this.showPartnerSelect = false;
    } else {
      this.loadPartners();
    }

    if (this.showHistory) {
      this.loadData();
    }
    this.textDialog = this.data?.textDialog ?? '';
  }

  loadPartners() {
    this.lmrService.suppliersBid().subscribe({
      next: (res: any) => {
        this.partners = res;
      },
      error: () => this.toastr.error('Erro ao carregar lista de parceiros.'),
    });
  }

  ngAfterViewInit(): void {}

  loadData(pageIndex: number = 1, pageSize: number = 10) {
    const params = {
      page: pageIndex,
      pageSize: pageSize,
      parceiroId: this.partnerSelected === 'TODOS' ? '' : this.partnerSelected,
    };

    this.lmrService.historySuppliers(params).subscribe((res: any) => {
      this.dataSource.data = res.page.map((item: any) => ({
        id: item.id,
        partner: item.parceiroNome,
        user: item.usuario,
        dateTime: new Date(item.dataHoraImportacao).toLocaleString('pt-BR'),
        downloadUrl: item.downloadUrl,
      }));

      if (this.paginator) {
        this.paginator.length = res.count;
      }
    });
  }

  importFile() {
    if (this.data.mode === 'full' && (!this.partnerSelected || this.partnerSelected === 'TODOS')) {
      this.toastr.error('Selecione um fornecedor para poder importar.');
      return;
    }

    if (!this.fileSelected) {
      this.toastr.error('Por favor selecione um arquivo para importar.');
      return;
    }

    const upload$ = this.data.importFn(
      this.fileSelected,
      this.partnerSelected !== 'TODOS' ? this.partnerSelected : undefined
    );

    upload$.subscribe({
      next: ({ sucesso, erro }: any) => {
        const resultRef = this.dialog.open(ImportResumeDialogComponent, {
          width: '550px',
          data: { sucesso, erro },
          disableClose: true,
        });

        resultRef.afterClosed().subscribe((action) => {
          if (action === 'confirm') {
            this.dialogRef.close(true);
          } else {
            this.fileSelected = null as any;
            this.fileName = '';
            this.partnerSelected = 'TODOS';

            if (this.fileInput) {
              this.fileInput.nativeElement.value = '';
            }

            if (this.showHistory) {
              if (this.paginator) {
                this.paginator.pageIndex = 0;
              }
              this.loadData(1, 10);
            }
          }
        });
      },
      error: (err: any) => {
        const message = err.errors?.[0] || 'Falha ao importar o arquivo.';
        this.toastr.error(message);
      },
    });
  }

  handlePageEvent(event: any) {
    this.loadData(event.pageIndex + 1, event.pageSize);
  }

  download(row: ImportItem) {
    const dateFormatted = row.dateTime
      .replace(/\//g, '_')
      .replace(/\s+às\s+/g, '_')
      .replace(/:/g, '_')
      .replace(/\s+/g, '_');

    const fileName = `Relatorio_${row.partner}_${dateFormatted}`;
    this.lmrService.downloadImportFile(row.id).subscribe({
      next: (data) => {
        Utils.createAndDownloadBlobFile(data, fileName, 'xlsx');
      },
      error: (err) => {
        const { errors } = Utils.arrayBufferToJson(err);
        this.toastr.error(errors || 'Ocorreu um erro ao exportar, tente novamente mais tarde.');
      },
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
